<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>顧客メモ</span>
      <v-btn
        color="primary"
        small
        @click="openCreateDialog"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiPlus }}
        </v-icon>
        メモ追加
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- カテゴリフィルター -->
      <v-select
        v-model="selectedCategory"
        :items="categoryOptions"
        label="カテゴリで絞り込み"
        clearable
        dense
        outlined
        class="mb-4"
        style="max-width: 200px;"
        @change="handleCategoryChange"
      />

      <!-- ローディング表示 -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <!-- タイムライン -->
      <div v-if="notes.length > 0">
        <v-timeline
          dense
          align-top
        >
          <v-timeline-item
            v-for="note in notes"
            :key="note.id"
            :color="getCategoryColor(note.category)"
            small
          >
            <v-card outlined>
              <v-card-text class="py-2">
                <div class="d-flex justify-space-between align-start mb-2">
                  <div>
                    <v-chip
                      x-small
                      :color="getCategoryColor(note.category)"
                      text-color="white"
                      class="mr-2"
                    >
                      {{ getCategoryLabel(note.category) }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{ getAuthorName(note) }} - {{ formatDateTime(note.createdAt) }}
                    </span>
                  </div>
                  <div v-if="note.canEdit">
                    <v-btn
                      icon
                      x-small
                      @click="openEditDialog(note)"
                    >
                      <v-icon small>
                        {{ icons.mdiPencil }}
                      </v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click="confirmDelete(note)"
                    >
                      <v-icon small>
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
                <p class="mb-0 text-body-2">
                  {{ note.content }}
                </p>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <!-- ページネーション -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="currentPage"
            :length="pagination.totalPages"
            :total-visible="7"
            @input="handlePageChange"
          />
        </div>
      </div>

      <!-- データなし -->
      <div
        v-else-if="!isLoading"
        class="text-center py-8 grey--text"
      >
        メモがありません
      </div>
    </v-card-text>

    <!-- メモ作成/編集ダイアログ -->
    <customer-note-form
      :dialog="noteFormDialog"
      :editing-note="editingNote"
      :categories="categoryOptions"
      @close="closeDialog"
      @save="handleSave"
    />

    <!-- 削除確認ダイアログ -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>メモの削除</v-card-title>
        <v-card-text>
          このメモを削除してもよろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            :loading="isDeleting"
            @click="handleDelete"
          >
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  computed, ref, watch,
} from '@vue/composition-api'
import { format } from 'date-fns'
import { mdiPlus, mdiPencil, mdiDelete } from '@mdi/js'
import useCustomerNotes, { NOTE_CATEGORIES } from '../composables/useCustomerNotes'
import CustomerNoteForm from './CustomerNoteForm.vue'

export default {
  name: 'CustomerNotes',
  components: {
    CustomerNoteForm,
  },
  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
  },
  setup(props, { root }) {
    const {
      notes,
      loading,
      pagination,
      selectedCategory,
      editingNote,
      noteFormDialog,
      getCategoryLabel,
      getCategoryColor,
      fetchNotes,
      createNote,
      updateNote,
      deleteNote,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      changePage,
      changeCategory,
    } = useCustomerNotes(root)

    const currentPage = ref(1)
    const deleteDialog = ref(false)
    const deleteTargetNote = ref(null)
    const isDeleting = ref(false)

    const isLoading = computed(() => loading.value)

    const categoryOptions = computed(() => [
      { value: null, text: 'すべて' },
      ...NOTE_CATEGORIES.map(c => ({ value: c.value, text: c.text })),
    ])

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd HH:mm')
      } catch (e) {
        return dateString
      }
    }

    const getAuthorName = note => {
      if (note && note.author && note.author.name) {
        return note.author.name
      }

      return '不明'
    }

    const handleCategoryChange = category => {
      currentPage.value = 1
      changeCategory(props.customerId, category)
    }

    const handlePageChange = page => {
      currentPage.value = page
      changePage(props.customerId, page)
    }

    const handleSave = async ({ content, category }) => {
      if (editingNote.value && editingNote.value.id) {
        // 更新
        await updateNote(props.customerId, editingNote.value.id, { content, category })
      } else {
        // 作成
        await createNote(props.customerId, { content, category })
      }
      closeDialog()
    }

    const confirmDelete = note => {
      deleteTargetNote.value = note
      deleteDialog.value = true
    }

    const handleDelete = async () => {
      if (!deleteTargetNote.value) return

      isDeleting.value = true
      await deleteNote(props.customerId, deleteTargetNote.value.id)
      isDeleting.value = false
      deleteDialog.value = false
      deleteTargetNote.value = null
    }

    // customerIdが変更されたら再取得
    watch(() => props.customerId, newId => {
      if (newId) {
        currentPage.value = 1
        fetchNotes(newId)
      }
    }, { immediate: true })

    return {
      notes,
      isLoading,
      pagination,
      selectedCategory,
      currentPage,
      editingNote,
      noteFormDialog,
      deleteDialog,
      isDeleting,
      categoryOptions,
      getCategoryLabel,
      getCategoryColor,
      formatDateTime,
      getAuthorName,
      handleCategoryChange,
      handlePageChange,
      handleSave,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      confirmDelete,
      handleDelete,
      icons: {
        mdiPlus,
        mdiPencil,
        mdiDelete,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
// タイムラインアイコンの背景色をテーマに合わせる
::v-deep .v-timeline-item__dot {
  // ライトモード: 白背景
  .theme--light & {
    background-color: #ffffff !important;
  }

  // ダークモード: ダーク背景
  .theme--dark & {
    background-color: #1e1e1e !important;
  }
}
</style>
